import { makeStyles, Typography } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import TextField from "@material-ui/core/TextField";
import { BannerCreate_bannerCreate_errors } from "@saleor/banners/types/BannerCreate";
import CardTitle from "@saleor/components/CardTitle";
import FormSpacer from "@saleor/components/FormSpacer";
import MultiAutocompleteSelectField from "@saleor/components/MultiAutocompleteSelectField";
import { MultiAutocompleteChoiceType } from "@saleor/components/MutliTagAutoCompleteField";
import RichTextEditor from "@saleor/components/RichTextEditor";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { ChangeEvent, FormChange } from "@saleor/hooks/useForm";
import { commonMessages } from "@saleor/intl";
import { maybe, splitDateTime } from "@saleor/misc";
import { SearchMalls_search_edges_node } from "@saleor/searches/types/SearchMalls";
import { SearchSales_search_edges_node } from "@saleor/searches/types/SearchSales";
import { SearchVouchers_search_edges_node } from "@saleor/searches/types/SearchVouchers";
import { FetchMoreProps } from "@saleor/types";
import { SmopBannerTypeEnum } from "@saleor/types/globalTypes";
import { getFormErrors } from "@saleor/utils/errors";
import getBannerErrorMessage from "@saleor/utils/errors/banner";
import { RawDraftContentState } from "draft-js";
import React from "react";
import { useIntl } from "react-intl";

import { BannerDetailsFormData } from "../BannerDetailsPage";

const useStyles = makeStyles(
  () => ({
    description: {
      marginLeft: "11px"
    },
    descriptionLabel: {
      color: "#0030FF"
    }
  }),
  { name: "BannerDetails" }
);

export interface BannerInfoProps {
  data: BannerDetailsFormData;
  disabled: boolean;
  errors: BannerCreate_bannerCreate_errors[];
  featuredInDisplayValue: MultiAutocompleteChoiceType[];
  featuredChoices: any[];
  setSelectedFeatured: React.Dispatch<
    React.SetStateAction<MultiAutocompleteChoiceType[]>
  >;
  localError: {
    endDate: string;
    endTime?: string;
    name: string;
    startDate: string;
    startTime?: string;
    verticals: string;
  };
  initialDescription?: RawDraftContentState;
  isEdit: boolean;
  malls?: SearchMalls_search_edges_node[];
  sales?: SearchSales_search_edges_node[];
  vouchers?: SearchVouchers_search_edges_node[];
  fetchMoreMalls: FetchMoreProps;
  fetchMalls: (query: string) => void;
  fetchSales: (query: string) => void;
  fetchVouchers: (query: string) => void;
  onChange: FormChange;
  handleNameChange: (event: ChangeEvent) => void;
  onFeaturedChange: (event: ChangeEvent) => void;
}

const BannerInfo: React.FC<BannerInfoProps> = props => {
  const {
    data,
    disabled,
    errors,
    featuredInDisplayValue,
    featuredChoices,
    initialDescription,
    isEdit,
    localError,
    malls,
    sales,
    vouchers,
    fetchSales,
    fetchVouchers,
    onFeaturedChange,
    onChange
  } = props;
  const intl = useIntl();
  const classes = useStyles("");

  const formErrors = getFormErrors(["name", "description"], errors || []);

  const saleChoices = maybe(() => sales, []).map(sale => ({
    description: sale.description,
    endDate: splitDateTime(maybe(() => sale.endDate, "")).date,
    endTime: "23:59",
    label: sale.name,
    startDate: splitDateTime(maybe(() => sale.startDate, "")).date,
    startTime: "00:00",
    value: sale.id
  }));

  const voucherChoices = maybe(() => vouchers, []).map(voucher => ({
    description: voucher.description,
    endDate: splitDateTime(maybe(() => voucher.endDate, "")).date,
    endTime: "23:59",
    label: voucher.code,
    startDate: splitDateTime(maybe(() => voucher.startDate, "")).date,
    startTime: "00:00",
    value: voucher.id
  }));

  const mallChoicesAdd = maybe(() => malls, []).map(mall => ({
    label: mall.name,
    value: mall.id
  }));

  const mallChoices = [{ label: "All Malls", value: "all" }, ...mallChoicesAdd];

  const handleChange = (event: React.ChangeEvent<any>) => {
    onChange(event);
    updateNameChange(event);
    updateDateChange(event, "startDate", "startDate");
    updateDateChange(event, "endDate", "endDate");
    updateDateChange(event, "startTime", "startTime");
    updateDateChange(event, "endTime", "endTime");
    updateDescriptionChange(event);
  };

  const updateNameChange = (event: React.ChangeEvent<any>) => {
    const newEvent = { target: { name: "", value: "" } };
    if (event.target.name === "mall") {
      newEvent.target.name = "mallName";
      newEvent.target.value = event.target.value
        ? mallChoices.find(mall => mall.value === event.target.value).label
        : "All Malls";
    } else if (event.target.name === "saleId") {
      newEvent.target.name = "saleName";
      newEvent.target.value = saleChoices.find(
        sale => sale.value === event.target.value
      ).label;
    } else if (event.target.name === "voucherId") {
      newEvent.target.name = "voucherName";
      newEvent.target.value = voucherChoices.find(
        voucher => voucher.value === event.target.value
      ).label;
    }
    onChange(newEvent);
  };

  const updateDateChange = (
    event: React.ChangeEvent<any>,
    targetName: string,
    key: string
  ) => {
    const newEvent = { target: { name: targetName, value: null } };
    if (event.target.name === "saleId") {
      newEvent.target.value = newEvent.target.value = saleChoices.find(
        sale => sale.value === event.target.value
      )[key];
      onChange(newEvent);
    } else if (event.target.name === "voucherId") {
      newEvent.target.value = voucherChoices.find(
        voucher => voucher.value === event.target.value
      )[key];
      onChange(newEvent);
    }
  };

  const updateDescriptionChange = (event: React.ChangeEvent<any>) => {
    const newEvent = { target: { name: "subDescription", value: null } };
    if (event.target.name === "saleId") {
      newEvent.target.value = newEvent.target.value = saleChoices.find(
        sale => sale.value === event.target.value
      ).description;
      onChange(newEvent);
    } else if (event.target.name === "voucherId") {
      newEvent.target.value = voucherChoices.find(
        voucher => voucher.value === event.target.value
      ).description;
      onChange(newEvent);
    }
  };

  const nameInput = () => {
    switch (data.type) {
      case SmopBannerTypeEnum.SALE:
        return (
          <SingleAutocompleteSelectField
            idInputTxt="searchSalesTxt"
            choices={saleChoices}
            displayValue={data.saleName || ""}
            fetchChoices={fetchSales}
            label="Title"
            placeholder={intl.formatMessage({
              defaultMessage: "Search Sales"
            })}
            onChange={handleChange}
            name="saleId"
            value={data.saleId}
          />
        );
      case SmopBannerTypeEnum.VOUCHER:
        return (
          <SingleAutocompleteSelectField
            idInputTxt="searchVouchersTxt"
            choices={voucherChoices}
            displayValue={data.voucherName || ""}
            fetchChoices={fetchVouchers}
            label="Title"
            placeholder={intl.formatMessage({
              defaultMessage: "Search Vouchers"
            })}
            onChange={handleChange}
            name="voucherId"
            value={data.voucherId}
          />
        );
      default:
        return (
          <TextField
            id="titleTxt"
            error={!!formErrors.name || !!localError.name}
            helperText={
              !!localError.name
                ? localError.name
                : getBannerErrorMessage(formErrors.name, intl)
            }
            fullWidth
            label={intl.formatMessage({
              defaultMessage: "Title",
              description: "banner title"
            })}
            name="name"
            value={data.name}
            onChange={onChange}
          />
        );
    }
  };

  return (
    <>
      <Card>
        <CardTitle
          title={intl.formatMessage(commonMessages.generalInformations)}
        />
        <CardContent>
          <SingleSelectField
            label={intl.formatMessage({
              defaultMessage: "Type"
            })}
            disabled={disabled || isEdit}
            onChange={onChange}
            choices={[
              { label: "Announcement", value: SmopBannerTypeEnum.ANNOUNCEMENT },
              { label: "Sale", value: SmopBannerTypeEnum.SALE },
              { label: "Voucher", value: SmopBannerTypeEnum.VOUCHER }
            ]}
            name="type"
            value={data.type}
          />
          <FormSpacer />
          {nameInput()}
          <FormSpacer />
          {data.type === SmopBannerTypeEnum.ANNOUNCEMENT ? (
            <RichTextEditor
              disabled={disabled}
              error={!!formErrors.description}
              helperText={getBannerErrorMessage(formErrors.description, intl)}
              initial={initialDescription}
              label={intl.formatMessage(commonMessages.description)}
              name="description"
              onChange={onChange}
            />
          ) : (
            <div className={classes.description}>
              {data.subDescription !== "" && (
                <>
                  <Typography
                    variant="body2"
                    className={classes.descriptionLabel}
                  >
                    Description
                  </Typography>
                  <Typography variant="body1">{data.subDescription}</Typography>
                </>
              )}
            </div>
          )}
          <FormSpacer />
          <MultiAutocompleteSelectField
            error={!!localError.verticals}
            helperText={localError.verticals}
            displayValues={featuredInDisplayValue}
            label={intl.formatMessage({
              defaultMessage: "Featured in"
            })}
            choices={featuredChoices}
            name="verticals"
            value={data.verticals}
            onChange={onFeaturedChange}
          />

          <FormSpacer />
        </CardContent>
      </Card>
    </>
  );
};

export default BannerInfo;
